import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';
import ScrollToTop from '@/components/base/ScrollToTop';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 91n.com 官方指南',
  description: '了解 91n.com 官方指南导航门户的品牌背景、核心优势、信誉保证与联系方式。',
  url: 'https://91n.com/about',
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '91n.com 官方指南',
  url: 'https://91n.com',
  description: '91n.com 第三方使用指南导航门户，致力于为用户提供全面、准确、及时的官方入口信息和使用指引。',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91n.com' },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://91n.com/about' },
  ],
};

const highlights = [
  {
    title: '信息准确',
    description: '持续追踪 91n.com 官方动态，确保所有入口地址和使用信息准确无误，让您用得放心。',
    icon: 'ri-check-double-line',
  },
  {
    title: '更新及时',
    description: '第一时间同步 91n.com 平台更新公告、地址变更等重要信息，不让您错过任何动态。',
    icon: 'ri-refresh-line',
  },
  {
    title: '内容全面',
    description: '涵盖入门指南、功能教程、安全须知等全方位内容，满足不同层次用户的需求。',
    icon: 'ri-stack-line',
  },
  {
    title: '用户至上',
    description: '以用户需求为导向，持续优化内容质量和浏览体验，让信息获取更高效便捷。',
    icon: 'ri-heart-line',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const formUrl = 'https://readdy.ai/api/form/d9pm6fpn2trhb35gvhp0';
      const formData = new FormData(form);
      formData.delete('website_alt');
      const body = new URLSearchParams(formData as unknown as Record<string, string>).toString();

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后再试';
        if (serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后再试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="关于我们 - 91n.com 官方指南"
        description="了解 91n.com 官方指南导航门户的品牌背景、核心优势、信誉保证与联系方式。致力于为用户提供全面、准确、及时的 91n.com 官方入口信息和使用指引。"
        keywords="91n.com,关于我们,91n.com官网,91n.com指南,联系方式"
        canonicalUrl="https://91n.com/about"
        breadcrumbs={[
          { name: '首页', path: '/' },
          { name: '关于我们', path: '/about' },
        ]}
        schemaData={[aboutSchema, orgSchema, breadcrumbSchema]}
      />

      <Navbar />

      <main className="pt-20">
        {/* Page Header */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600 mb-6">
              <i className="ri-building-2-line text-2xl"></i>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-4">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              91n.com 官方指南是一个专注于为用户提供 91n.com 官方入口信息和使用指南的第三方导航门户。
              我们致力于帮助广大用户快速找到正确的访问地址，掌握平台的核心功能，安全、高效地使用 91n.com 的各项服务。
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-5">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    随着互联网的快速发展，越来越多的用户开始使用 91n.com 平台。然而，网络上充斥着各种仿冒网站和虚假信息，给用户带来了极大的困扰和安全隐患。
                  </p>
                  <p>
                    正是基于这样的背景，我们创建了 91n.com 官方指南导航门户。我们的使命是成为用户与 91n.com 之间最值得信赖的桥梁，提供最准确、最及时、最全面的官方入口信息和使用指引。
                  </p>
                  <p>
                    自上线以来，我们始终坚持以用户需求为中心，不断丰富内容体系，优化浏览体验。目前已经建立了涵盖入门指南、功能教程、常见问题、安全须知等多个版块的完整内容体系。
                  </p>
                </div>
              </div>
              <div className="aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20workspace%20with%20team%20collaboration%20concept%2C%20warm%20natural%20lighting%2C%20clean%20aesthetic%20with%20wooden%20desk%20and%20digital%20devices%2C%20professional%20editorial%20photography%20with%20soft%20shadows%20and%20organic%20composition&width=800&height=600&seq=about-brand-image&orientation=landscape"
                  alt="91n.com 官方指南品牌介绍"
                  className="w-full h-full object-cover object-top rounded-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Highlights */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-10 text-center">
              为什么信赖我们
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {highlights.map((item, index) => (
                <div key={index} className="flex gap-4 bg-background-50 rounded-xl p-6">
                  <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-foreground-950 mb-1.5 font-heading">
                      {item.title}
                    </h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600">
                如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
              <div className="bg-background-100 rounded-xl p-5 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-1 font-heading">电子邮箱</h4>
                <p className="text-xs text-foreground-500">contact@91n-guide.com</p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-1 font-heading">响应时间</h4>
                <p className="text-xs text-foreground-500">24小时内回复</p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-global-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-1 font-heading">官方网站</h4>
                <a
                  href="https://91n.com"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-xs text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  91n.com
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-background-100 rounded-xl p-6 md:p-8">
              <h3 className="text-lg font-semibold text-foreground-950 font-heading mb-6">
                发送消息
              </h3>
              {formStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-950 font-medium mb-1">消息已发送！</p>
                  <p className="text-sm text-foreground-500">感谢您的来信，我们会尽快回复。</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-5">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题 <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的消息内容（不超过500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  {formStatus === 'error' && formError && (
                    <div className="text-sm text-red-600 bg-red-50 px-4 py-2.5 rounded-lg">
                      {formError}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-send-plane-line"></i>
                    发送消息
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}